import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { toPng, toBlob } from "html-to-image";
import { Trophy, ArrowDown, Lock, CheckCircle2, Mail, CalendarClock, Download, Share2 } from "lucide-react";
import { api } from "../lib/api";
import { ASSETS } from "../lib/constants";
import { Marquee } from "../components/Marquee";
import { MatchCard } from "../components/MatchCard";

const HEAD_LINES = ["OBERLIGA WESTFALEN", "TIPP-ARENA"];

function HeroReveal() {
  const last = HEAD_LINES.length - 1;
  return (
    <h1 className="font-head font-bold uppercase tracking-tight leading-[0.95] text-white text-5xl sm:text-7xl lg:text-8xl break-words drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
      {HEAD_LINES.map((line, i) => (
        <span key={i} className="reveal-mask">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className={i === last ? "text-[#e9b44c]" : "text-white"}>{line}</span>
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

const CHAPTERS = [
  { n: "01", t: "Tippen", d: "Gib für jedes Spiel des Spieltags dein Ergebnis ein – direkt gegen den Tipp unseres heutigen Gast-Tippers. 💪" },
  { n: "02", t: "Absenden", d: "Rankingname und E-Mail eintragen (Instagram optional), Teilnahmebedingungen bestätigen, Tipps absenden." },
  { n: "03", t: "Punkten", d: "3 Punkte für den exakten Tipp, 2,5 für die richtige Tordifferenz, 2 für die richtige Tendenz – schlag den Protagonisten der Woche!" },
];

function fmtDeadline(dl) {
  if (!dl) return "";
  try {
    return new Date(dl).toLocaleString("de-DE", {
      weekday: "long", day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }) + " Uhr";
  } catch { return ""; }
}

function useCountdown(deadline) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadline) return null;
  const diff = new Date(deadline).getTime() - now;
  if (diff <= 0) return { over: true };
  return {
    over: false,
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

function DeadlineBanner({ deadline, accepting, gameOpen }) {
  const cd = useCountdown(deadline);
  const closed = !accepting;
  if (closed) {
    return (
      <div className="glass rounded-2xl p-5 mb-8 border-[#e9b44c]/30 flex items-center gap-3" data-testid="closed-banner">
        <Lock size={18} className="text-[#e9b44c] shrink-0" />
        <p className="text-sm text-[#cbd5e1]">
          {gameOpen === false
            ? "Das Tippspiel ist aktuell geschlossen. Schau bald wieder vorbei!"
            : `Annahmeschluss vorbei – seit ${fmtDeadline(deadline)} können keine Tipps mehr abgegeben werden.`}
        </p>
      </div>
    );
  }
  if (!deadline) return null;
  const box = (v, l) => (
    <div className="text-center px-3">
      <p className="font-head text-3xl sm:text-4xl leading-none text-white tabular-nums">{String(v).padStart(2, "0")}</p>
      <p className="text-xs tracking-[0.12em] uppercase text-[#cbd5e1] mt-1 font-semibold">{l}</p>
    </div>
  );
  return (
    <div className="glass rounded-2xl p-5 mb-8 flex flex-col items-center text-center gap-4" data-testid="deadline-banner">
      <div className="text-center">
        <p className="font-head text-2xl sm:text-3xl tracking-[0.08em] uppercase text-[#e9b44c] font-bold leading-none">Annahmeschluss</p>
        <p className="text-xl sm:text-2xl text-white font-semibold mt-2" data-testid="deadline-text">{fmtDeadline(deadline)}</p>
      </div>
      {cd && !cd.over && (
        <div className="flex items-center justify-center divide-x divide-white/10">
          {box(cd.d, "Tage")}{box(cd.h, "Std")}{box(cd.m, "Min")}{box(cd.s, "Sek")}
        </div>
      )}
    </div>
  );
}

function RankingButton({ testid }) {
  return (
    <Link
      to="/spieleruebersicht"
      className="btn-primary rounded-full px-12 sm:px-16 py-5 sm:py-6 flex flex-col items-center leading-none shadow-2xl shadow-[#e9b44c]/30 hover:scale-[1.03] transition-transform"
      data-testid={testid}
    >
      <span className="font-head uppercase tracking-wide text-2xl sm:text-4xl">Wochenranking</span>
      <span className="text-sm sm:text-base font-bold tracking-wide normal-case mt-2 text-[#01122f]">ab Sonntag 21:00 Uhr</span>
    </Link>
  );
}

export default function Landing() {
  const [config, setConfig] = useState(null);
  const [matches, setMatches] = useState([]);
  const [accepting, setAccepting] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tips, setTips] = useState({});
  const [form, setForm] = useState({ instagram_name: "", first_name: "", email: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [emailSent, setEmailSent] = useState(true);
  const [participantCount, setParticipantCount] = useState(0);
  const tippenRef = useRef(null);

  const loadState = () => {
    setLoading(true);
    setLoadError(false);
    api.get("/public/state", { timeout: 12000 })
      .then(({ data }) => {
        setConfig(data.config);
        setMatches(data.matches || []);
        setAccepting(!!data.accepting);
        setParticipantCount(data.participant_count || 0);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadState(); }, []);

  const gameOpen = accepting;

  const onTipChange = (id, side, value) => {
    const v = value === "" ? "" : String(Math.max(0, Math.min(99, parseInt(value, 10) || 0)));
    setTips((p) => ({ ...p, [id]: { ...p[id], [side]: v } }));
  };

  const filledCount = useMemo(
    () => matches.filter((m) => tips[m.id]?.home !== "" && tips[m.id]?.home != null && tips[m.id]?.away !== "" && tips[m.id]?.away != null).length,
    [tips, matches]
  );
  const allTipped = matches.length > 0 && filledCount === matches.length;
  const remaining = matches.length - filledCount;

  const scrollToTippen = () => {
    const el = tippenRef.current;
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="tippschein"]');
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
      else el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => clearTimeout(t);
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Bitte akzeptiere die Teilnahmebedingungen.");
    if (!form.first_name || !form.email) return toast.error("Bitte fülle Rankingname und E-Mail aus.");
    const payloadTips = matches
      .filter((m) => tips[m.id]?.home !== "" && tips[m.id]?.home != null && tips[m.id]?.away !== "" && tips[m.id]?.away != null)
      .map((m) => ({ match_id: m.id, tip_home: parseInt(tips[m.id].home, 10), tip_away: parseInt(tips[m.id].away, 10) }));
    if (payloadTips.length !== matches.length) return toast.error(`Bitte tippe alle Spiele – noch ${matches.length - payloadTips.length} offen.`);

    setSubmitting(true);
    try {
      const { data } = await api.post("/submissions", { ...form, tips: payloadTips });
      setEmailSent(data.email_sent);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Etwas ist schiefgelaufen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="grain relative min-h-screen flex flex-col items-center justify-center gap-6" data-testid="landing-loading">
        <img src={ASSETS.logo} alt="TSG Sprockhövel" className="h-24 w-auto object-contain animate-pulse" />
        <div className="h-8 w-8 rounded-full border-2 border-[#e9b44c]/30 border-t-[#e9b44c] animate-spin" />
      </div>
    );
  }

  if (loadError || !config) {
    return (
      <div className="grain relative min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center" data-testid="landing-error">
        <img src={ASSETS.logo} alt="TSG Sprockhövel" className="h-24 w-auto object-contain" />
        <div>
          <p className="font-head text-2xl uppercase text-white mb-2">Seite konnte nicht geladen werden</p>
          <p className="text-sm text-[#9fb2cc] max-w-sm mx-auto">Bitte versuche es in einem Moment erneut. Falls das Problem bestehen bleibt, liegt evtl. eine kurzzeitige Störung vor.</p>
        </div>
        <button onClick={loadState} className="btn-primary rounded-full px-8 py-3.5 text-sm" data-testid="retry-btn">
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="grain relative min-h-screen">
      {/* Nav */}
      <nav className="relative z-20 flex justify-center px-5 sm:px-10 py-6">
        <RankingButton testid="ranking-link" />
      </nav>

      <Marquee opponent={config?.opponent_name} prize={config?.prize} />

      {/* Hero */}
      <header className="relative z-10 px-5 sm:px-10 pt-8 pb-16 min-h-[88vh] flex flex-col justify-center overflow-hidden">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.heroBg})` }}
        />
        <div className="absolute inset-0 -z-10 bg-[#01122f]/85" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#01122f] via-transparent to-[#01122f]/60" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#01122f] via-[#01122f]/70 to-transparent" />

        <div className="max-w-3xl mx-auto w-full flex flex-col items-center text-center text-readable">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mb-6 flex items-center justify-center gap-4 sm:gap-7"
          >
            <img src={ASSETS.logo} alt="TSG Sprockhövel" className="h-16 sm:h-24 w-auto" />
            <img src={ASSETS.oberliga} alt="Oberliga Westfalen" className="h-24 sm:h-32 w-auto" data-testid="oberliga-logo-hero" />
            <img src={ASSETS.logo} alt="TSG Sprockhövel" className="h-16 sm:h-24 w-auto" />
          </motion.div>

          <HeroReveal />

          {participantCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="glass rounded-3xl px-8 py-6 sm:px-14 sm:py-9 mt-8 flex flex-col items-center border border-[#e9b44c]/50 shadow-2xl shadow-[#e9b44c]/25"
              data-testid="participant-counter"
            >
              <p className="text-base sm:text-xl tracking-[0.2em] uppercase text-[#e2e8f0] font-bold text-center">
                Diese Woche tippen bereits
              </p>
              <motion.span
                animate={{ scale: [1, 1.07, 1] }}
                transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                className="font-head text-7xl sm:text-9xl text-[#e9b44c] leading-none my-3 tabular-nums drop-shadow-[0_0_30px_rgba(233,180,76,0.45)]"
                data-testid="participant-count"
              >
                {participantCount}
              </motion.span>
              <p className="font-head text-3xl sm:text-5xl uppercase tracking-wide text-white">Mitstreiter</p>
              <p className="mt-4 text-lg sm:text-2xl font-bold text-[#e9b44c] uppercase tracking-[0.15em]">Mach' mit!</p>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-7"
            data-testid="subline"
          >
            <p className="text-lg sm:text-2xl tracking-[0.15em] uppercase text-[#e9b44c] font-bold mb-3">Heute mit bzw. gegen</p>
            <p className="font-head uppercase leading-none text-white text-5xl sm:text-6xl tracking-tight" data-testid="opponent-name">
              {config.opponent_name}
            </p>
            {config?.opponent_position && (
              <p className="text-xl sm:text-3xl text-[#cbd5e1] mt-3 font-semibold" data-testid="opponent-position">{config.opponent_position}</p>
            )}
          </motion.div>

          {/* Opponent photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="relative mt-10 w-full max-w-sm"
          >
            <div className="absolute -inset-6 rounded-full bg-[#e9b44c]/20 blur-3xl" />
            <div className="relative glass rounded-3xl overflow-hidden text-left">
              <img
                src={config?.opponent_image || ASSETS.trainer}
                alt={config?.opponent_name || "Gegner"}
                className="w-full h-[320px] object-cover object-top"
                data-testid="opponent-photo"
              />
              <div className="bg-[#0a2148] border-t border-white/10 px-5 py-4 text-center">
                <p className="font-head text-lg sm:text-xl tracking-[0.12em] uppercase text-[#e9b44c] font-bold mb-1">Dein Gegner heute</p>
                <p className="font-head text-2xl sm:text-3xl uppercase leading-none">{config.opponent_name}</p>
                {config?.opponent_position && <p className="text-xs text-[#9fb2cc] mt-1.5">{config.opponent_position}</p>}
              </div>
            </div>
          </motion.div>

          {config?.opponent_bio && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-6 w-full max-w-sm"
              data-testid="opponent-bio"
            >
              <div className="glass rounded-3xl p-6 text-left">
                <p className="text-base sm:text-lg tracking-[0.18em] uppercase text-[#e9b44c] font-bold mb-3">Zur Person</p>
                <p className="text-xl text-[#e2e8f0] leading-relaxed whitespace-pre-line">{config.opponent_bio}</p>
              </div>
            </motion.div>
          )}

          {config?.prize && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mt-10"
            >
              <p className="text-sm sm:text-base tracking-[0.14em] uppercase font-bold text-[#e9b44c] mb-2 flex items-center justify-center gap-2">
                <Trophy size={18} className="text-[#e9b44c]" /> Diese Woche zu gewinnen:
              </p>
              <p className="font-head text-2xl sm:text-4xl uppercase tracking-tight text-white" data-testid="prize-hero">
                {config.prize}
              </p>
              {config?.prize_image && (
                <div className="mt-6 mx-auto w-full max-w-sm">
                  <div className="relative glass rounded-3xl overflow-hidden p-2">
                    <img
                      src={config.prize_image}
                      alt={config.prize}
                      className="w-full rounded-2xl object-cover"
                      data-testid="prize-image-hero"
                    />
                  </div>
                </div>
              )}
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-10 flex flex-col items-center gap-3"
          >
            <button onClick={scrollToTippen} className="btn-primary rounded-full px-12 sm:px-16 py-5 sm:py-6 text-lg sm:text-2xl font-bold flex items-center gap-3 shadow-2xl shadow-[#e9b44c]/30 hover:scale-[1.03] transition-transform" data-testid="cta-start">
              Jetzt tippen <ArrowDown size={24} />
            </button>
            <span className="text-xs tracking-[0.15em] uppercase text-[#9fb2cc]">
              {config?.matchday_label}
            </span>
          </motion.div>
        </div>
      </header>

      {/* Manifesto */}
      <section className="relative z-10 px-5 sm:px-10 pt-16 sm:pt-20 pb-8 max-w-6xl mx-auto text-center">
        <p className="text-sm sm:text-base tracking-[0.15em] uppercase text-[#e9b44c] font-bold mb-10">So funktioniert's</p>
        <div className="grid sm:grid-cols-3 gap-6">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="glass rounded-2xl p-7 flex flex-col items-center"
            >
              <p className="font-head text-5xl text-[#e9b44c]/70 mb-4">{c.n}</p>
              <h3 className="font-head text-2xl uppercase mb-2">{c.t}</h3>
              <p className="text-base text-[#cbd5e1] leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Matches + form */}
      <section ref={tippenRef} className="relative z-10 px-5 sm:px-10 pt-4 pb-16 sm:pb-24 max-w-5xl mx-auto scroll-mt-6">
        {done ? (
          <SuccessCard trainer={config?.opponent_name} emailSent={emailSent} email={form.email} name={form.first_name} matches={matches} tips={tips} config={config} />
        ) : (
          <>
            <div className="flex flex-col items-center text-center gap-4 mb-8">
              <img src={ASSETS.oberliga} alt="Oberliga Westfalen" className="h-36 sm:h-44 w-auto" data-testid="oberliga-logo-section" />
              <div>
                <p className="text-sm sm:text-base tracking-[0.15em] uppercase text-[#e9b44c] font-bold mb-2">Der Spieltag</p>
                <h2 className="font-head text-4xl sm:text-5xl uppercase tracking-tight leading-none">Oberliga Westfalen</h2>
                <h2 className="font-head text-4xl sm:text-5xl uppercase tracking-tight leading-none text-[#e9b44c] mt-1" data-testid="matchday-value">{config?.matchday_label || "Spieltag"}</h2>
              </div>
              <div className="inline-flex items-baseline gap-2">
                <span className="font-head text-4xl text-[#e9b44c]" data-testid="filled-count">{filledCount}/{matches.length}</span>
                <span className="text-sm tracking-[0.12em] uppercase text-[#cbd5e1] font-semibold">getippt</span>
              </div>
            </div>

            {config && <DeadlineBanner deadline={config.deadline} accepting={accepting} gameOpen={config.game_open} />}

            <div className="grid sm:grid-cols-2 gap-4">
              {matches.map((m, i) => (
                <MatchCard key={m.id} match={m} index={i} tip={tips[m.id]} onChange={onTipChange} disabled={!gameOpen} opponentName={config?.opponent_name} />
              ))}
            </div>

            {/* Form */}
            <form onSubmit={submit} className="glass rounded-3xl p-6 sm:p-10 mt-10" data-testid="participation-form">
              <h3 className="font-head text-3xl uppercase mb-1 text-center">Meine Daten</h3>
              <p className="text-sm text-[#9fb2cc] mb-7 text-center">Trage deine Daten ein und sende deine Tipps ab.</p>

              <div className="grid gap-4">
                <input className="field" placeholder="Rankingname (Pflicht)" value={form.first_name} disabled={!gameOpen}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="input-firstname" />
                <input className="field" placeholder="Instagram-Name (optional)" value={form.instagram_name} disabled={!gameOpen}
                  onChange={(e) => setForm({ ...form, instagram_name: e.target.value })} data-testid="input-instagram" />
                <input className="field" type="email" placeholder="E-Mail (Pflicht)" value={form.email} disabled={!gameOpen}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
              </div>

              <label className="flex items-start gap-3 mt-6 cursor-pointer select-none">
                <input type="checkbox" checked={form.consent} disabled={!gameOpen}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  className="mt-1 h-5 w-5 accent-[#e9b44c] shrink-0" data-testid="input-consent" />
                <span className="text-base text-[#e2e8f0] leading-relaxed">
                  Ich akzeptiere die Datenschutzbestimmungen und Teilnahmebedingungen und bin mit der Verarbeitung meiner Daten zum Zweck des Tippspiels einverstanden.
                </span>
              </label>

              <button type="submit" disabled={!gameOpen || submitting || !allTipped || !form.consent} className="btn-primary w-full py-5 mt-8 text-lg" data-testid="submit-btn">
                {submitting ? "Wird gesendet…" : !allTipped ? `Noch ${remaining} ${remaining === 1 ? "Spiel" : "Spiele"} tippen` : !form.consent ? "Bitte Teilnahmebedingungen akzeptieren" : "Tipps absenden"}
              </button>
            </form>
          </>
        )}
      </section>

      {config && (config.whatsapp_url || config.instagram_url) && (
        <section className="relative z-10 px-5 sm:px-10 pb-16 max-w-3xl mx-auto text-center">
          <div className="glass rounded-3xl p-8 sm:p-10">
            <h2 className="font-head text-3xl sm:text-4xl uppercase mb-2">Bleib am Ball</h2>
            <p className="text-base text-[#cbd5e1] mb-6">Verpasse keinen Spieltag – werde Teil der TSG-Community.</p>
            <SocialLinks whatsapp={config.whatsapp_url} instagram={config.instagram_url} />
          </div>
        </section>
      )}

      <section className="relative z-10 px-5 sm:px-10 pb-16 max-w-2xl mx-auto text-center" data-testid="winner-info">
        <div className="glass rounded-3xl p-7 sm:p-9 border border-[#e9b44c]/25">
          <p className="text-sm sm:text-base tracking-[0.14em] uppercase font-bold text-[#e9b44c] mb-3 flex items-center justify-center gap-2">
            <Trophy size={18} className="text-[#e9b44c]" /> Wichtig
          </p>
          <p className="text-base sm:text-lg text-[#e2e8f0] leading-relaxed">
            Der Gewinner wird im <span className="text-white font-semibold">WhatsApp-Kanal</span> bekannt gegeben und erhält
            eine DM via <span className="text-white font-semibold">Instagram</span> oder <span className="text-white font-semibold">E-Mail</span>.
            Deswegen: <span className="text-[#e9b44c] font-semibold">Folg uns auf beiden Kanälen!</span> 💪
          </p>
        </div>
      </section>

      <section className="relative z-10 px-5 sm:px-10 pb-16 flex justify-center" data-testid="ranking-cta-bottom">
        <RankingButton testid="ranking-link-bottom" />
      </section>

      <footer className="relative z-10 border-t border-white/10 px-5 sm:px-10 py-10 mt-10">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="TSG Sprockhövel" className="h-11 w-auto object-contain" />
            <p className="text-xs text-[#9fb2cc]">© {new Date().getFullYear()} TSG Sprockhövel · Tip-Battle</p>
          </div>
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <a href="/impressum" target="_blank" rel="noopener noreferrer" className="text-[#9fb2cc] hover:text-[#e9b44c] transition-colors" data-testid="footer-impressum">Impressum</a>
            <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#9fb2cc] hover:text-[#e9b44c] transition-colors" data-testid="footer-datenschutz">Datenschutz</a>
            <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-[#9fb2cc] hover:text-[#e9b44c] transition-colors" data-testid="footer-agb">AGB</a>
            <Link to="/admin" className="text-[#9fb2cc] hover:text-[#e9b44c] transition-colors">Adminbereich</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.549 4.142 1.595 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.582 0 11.94-5.359 11.944-11.893a11.821 11.821 0 00-3.417-8.372"/>
    </svg>
  );
}

function InstagramIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  );
}

function SocialLinks({ whatsapp, instagram }) {
  if (!whatsapp && !instagram) return null;
  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
      {whatsapp && (
        <a href={whatsapp} target="_blank" rel="noopener noreferrer" className="px-6 py-3.5 text-sm font-semibold rounded-full flex items-center gap-2.5 text-white bg-[#25D366] hover:bg-[#1ebe5b] transition-colors shadow-lg shadow-[#25D366]/25" data-testid="whatsapp-btn">
          <WhatsAppIcon size={19} /> WhatsApp-Kanal beitreten
        </a>
      )}
      {instagram && (
        <a href={instagram} target="_blank" rel="noopener noreferrer" className="px-6 py-3.5 text-sm font-semibold rounded-full flex items-center gap-2.5 text-white transition-opacity hover:opacity-90 shadow-lg shadow-[#dc2743]/25" style={{ background: "linear-gradient(45deg, #feda75, #fa7e1e, #d62976, #962fbf, #4f5bd5)" }} data-testid="instagram-btn">
          <InstagramIcon size={19} /> Instagram folgen
        </a>
      )}
    </div>
  );
}

function SuccessCard({ trainer, emailSent, email, name, matches = [], tips = {}, config }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  const slug = (name || "tipp").replace(/\s+/g, "-").toLowerCase();
  const shareText = `Meine Tipps für ${config?.matchday_label || "den Spieltag"} in der TSG Oberliga-Westfalen Tipp-Arena! ⚽ Mach mit: ${window.location.origin}`;

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#01122f" });
      const link = document.createElement("a");
      link.download = `tsg-tippschein-${slug}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Bild gespeichert!");
    } catch {
      toast.error("Bild konnte nicht erstellt werden.");
    } finally {
      setDownloading(false);
    }
  };

  const share = async () => {
    if (!cardRef.current) return;
    setSharing(true);
    try {
      const blob = await toBlob(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#01122f" });
      const file = new File([blob], `tsg-tippschein-${slug}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Mein Tippschein", text: shareText });
      } else if (navigator.share) {
        await navigator.share({ title: "Mein Tippschein", text: shareText, url: window.location.origin });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = file.name; a.click();
        URL.revokeObjectURL(url);
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
        toast.info("Bild gespeichert – du kannst es jetzt posten.");
      }
    } catch (e) {
      if (e && e.name !== "AbortError") toast.error("Teilen nicht möglich – Bild wurde ggf. gespeichert.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass rounded-3xl p-8 sm:p-14 text-center"
      data-testid="success-message"
    >
      <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-[#e9b44c]/15 border border-[#e9b44c]/40 mb-7">
        <CheckCircle2 size={40} className="text-[#e9b44c]" />
      </div>
      <p className="text-xs tracking-[0.25em] uppercase text-[#e9b44c] font-bold mb-3">Danke für deine Teilnahme</p>
      <h2 className="font-head text-4xl sm:text-5xl uppercase tracking-tight mb-4">Tipps abgegeben!</h2>
      <p className="text-lg text-[#cbd5e1] max-w-lg mx-auto">
        Deine Tipps wurden erfolgreich abgegeben. Viel Erfolg gegen{" "}
        <span className="text-white font-semibold">{trainer || "unseren Wochen-Protagonisten"}</span>!
      </p>
      {emailSent && (
        <p className="mt-6 inline-flex items-center gap-2 text-sm text-[#9fb2cc]">
          <Mail size={15} className="text-[#e9b44c]" /> Wir haben dir eine Bestätigung mit deinen Ergebnissen an {email} gesendet.
        </p>
      )}

      {/* Herunterladbarer Tippschein */}
      <div className="mt-10 flex flex-col items-center">
        <div
          ref={cardRef}
          className="mx-auto w-full max-w-sm rounded-3xl overflow-hidden text-left"
          style={{ background: "#01122f", border: "1px solid rgba(233,180,76,0.3)" }}
          data-testid="tippschein"
        >
          <div style={{ background: "#0a2148" }} className="px-6 py-5 flex items-center gap-3">
            <img src={ASSETS.logo} alt="TSG" className="h-12 w-auto object-contain" crossOrigin="anonymous" />
            <div>
              <p className="font-head uppercase text-white text-lg leading-none">Oberliga Westfalen</p>
              <p className="font-head uppercase text-[#e9b44c] text-xl leading-none mt-1">Tipp-Arena</p>
            </div>
          </div>
          <div className="px-6 py-5">
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#e9b44c] font-bold mb-1">Tippschein</p>
            <p className="font-head text-2xl uppercase text-white leading-none">{name || "Teilnehmer"}</p>
            {config?.matchday_label && <p className="text-xs text-[#9fb2cc] mt-1 mb-4">{config.matchday_label}</p>}
            <div className="space-y-2 mt-3">
              {matches.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2" style={{ background: "rgba(255,255,255,0.05)" }}>
                  <span className="text-sm text-white truncate">{m.home} <span className="text-[#9fb2cc]">–</span> {m.away}</span>
                  <span className="font-head text-lg tabular-nums text-[#e9b44c] shrink-0">{tips[m.id]?.home}:{tips[m.id]?.away}</span>
                </div>
              ))}
            </div>
            {config?.opponent_name && (
              <p className="text-xs text-[#9fb2cc] mt-4">Mein Duell gegen: <span className="text-white font-semibold">{config.opponent_name}</span></p>
            )}
            {config?.prize && (
              <p className="text-xs text-[#9fb2cc] mt-1">Zu gewinnen: <span className="text-[#e9b44c] font-semibold">{config.prize}</span></p>
            )}
            <p className="text-[10px] text-[#64748b] mt-4 uppercase tracking-widest">TSG Sprockhövel · Tipp-Arena</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
          <button onClick={share} disabled={sharing} className="px-7 py-3.5 text-sm font-semibold rounded-full flex items-center gap-2 text-white bg-[#25D366] hover:bg-[#1ebe5b] transition-colors shadow-lg shadow-[#25D366]/25" data-testid="share-tippschein-btn">
            <Share2 size={16} /> {sharing ? "Wird geteilt…" : "Teilen (WhatsApp/Instagram)"}
          </button>
          <button onClick={download} disabled={downloading} className="btn-ghost rounded-full px-7 py-3.5 text-sm flex items-center gap-2" data-testid="download-tippschein-btn">
            <Download size={16} /> {downloading ? "Erstelle Bild…" : "Als Bild herunterladen"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
